import { recordInformation } from "components/DetailPage/RecordInformation";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import React from "react";
import CompanyGoogleMap from "./detail/components/CompanyGoogleMap";
import { bankDetailSection } from "./sections/bankDetailSection";
import { businessCentralSection } from "./sections/businessCentralSection";
import { financialDetailSection } from "./sections/financialDetailSection";
import { companyMainSection } from "./sections/main";
import { venueDetailsSection } from "./sections/venueDetailSection";
import { hubSpotSection } from "./sections/hubSpotSection";
import { SectionTemplateHeader } from "components/MainSectionTemplate/SectionTemplateHeader";
import { BankFormState, VenueFormState } from "./detail/components/models";
import { contactDetailSection } from "./sections/contactDetailSection";
import { convertToFormState, convertToVenueFormState, convertToBankFormState } from "./detail/utils";
import { useQueryClient } from "@tanstack/react-query";
import { CompanyApi } from "queries/Company";

interface CompanyDetailSectionProps {
  company: server.dto.Company
  venueDetails: server.dto.VenueDetails
  displayBusinessCentralNumber: boolean
  displayHubSpotPanel: boolean
}

export const CompanyDetailSection = (props: CompanyDetailSectionProps) => {
  const { displayBusinessCentralNumber, displayHubSpotPanel } = props;
  const [company, setCompany] = React.useState<server.dto.Company>(props.company);
  const [venueDetails, setVenueDetails] = React.useState<server.dto.VenueDetails>(props.venueDetails);
  const [companyFormState, setCompanyFormState] = React.useState(convertToFormState(company));
  const [venueFormState, setVenueFormState] = React.useState<VenueFormState>(convertToVenueFormState(venueDetails));
  const [bankFormState, setBankFormState] = React.useState<BankFormState>(convertToBankFormState(company.bank));
  const [tags, setTags] = React.useState(company.tags || []);

  const onValueChanged = async (key: string, value: any) => {
    if (key === "visitingAddress" || key === "postalAddress") {
      setCompanyFormState(prevState => ({
        ...prevState,
        address: value,
      }));
    } else if (key === "profile") {
      setCompanyFormState(prevState => ({
        ...prevState,
        profile: value,
      }));
    } else if (key === "status") {
      setCompanyFormState(prevState => ({
        ...prevState,
        status: value,
      }));
    } else if (key === "tags") {
      setTags(value);
    } else if (key === "website") {
      setCompanyFormState(prevState => ({
        ...prevState,
        website: value,
      }));
    } else if (key === "phone") {
      setCompanyFormState(prevState => ({
        ...prevState,
        phone: value,
      }));
    } else if (key === "facebook") {
      setCompanyFormState(prevState => ({
        ...prevState,
        facebook: value,
      }));
    } else if (key === "networkPath") {
      setCompanyFormState(prevState => ({
        ...prevState,
        networkPath: value,
      }));
    } else if (key === "additional") {
      setCompanyFormState(prevState => ({
        ...prevState,
        additional: value,
      }));
    } else if (key === "owner") {
      setCompanyFormState(prevCompany => ({
        ...prevCompany,
        owner: value,
      }));
    } else if (key === "financialRisk") {
      setCompanyFormState(prevState => ({
        ...prevState,
        financialRisk: value,
      }));
    } else if (key === "euVatNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        euVatNumber: value,
      }));
    } else if (key === "globalLocationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        globalLocationNumber: value,
      }));
    } else if (key === "fiscalNumber1") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber1: value,
      }));
    } else if (key === "fiscalNumber2") {
      setCompanyFormState(prevState => ({
        ...prevState,
        fiscalNumber2: value,
      }));
    } else if (key === "businessRegistrationNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        businessRegistrationNumber: value,
      }));
    } else if (key === "capacity") {
      setVenueFormState(prevState => ({
        ...prevState,
        capacity: value ?? 0,
      }));
    }
    else if (key === "ageRestriction") {
      setVenueFormState(prevState => ({
        ...prevState,
        ageRestriction: value,
      }));
    } else if (key === "doorsOpen") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          open: value,
        },
      }));
    } else if (key === "doorsClose") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: {
          ...prevState.doors,
          close: value,
        },
      }));
    } else if (key === "category") {
      setVenueFormState(prevState => ({
        ...prevState,
        category: value,
      }));
    } else if (key === "stage") {
      setVenueFormState(prevState => ({
        ...prevState,
        stage: value,
      }));
    } else if (key === "vehicleAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        vehicleAccess: value,
      }));
    } else if (key === "parking") {
      setVenueFormState(prevState => ({
        ...prevState,
        parking: value,
      }));
    } else if (key === "gearAccess") {
      setVenueFormState(prevState => ({
        ...prevState,
        gearAccess: value,
      }));
    } else if (key === "power") {
      setVenueFormState(prevState => ({
        ...prevState,
        power: value,
      }));
    } else if (key === "lighting") {
      setVenueFormState(prevState => ({
        ...prevState,
        lighting: value,
      }));
    } else if (key === "dressingRooms") {
      setVenueFormState(prevState => ({
        ...prevState,
        dressingRooms: value,
      }));
    } else if (key === "noiseRestrictions") {
      setVenueFormState(prevState => ({
        ...prevState,
        noiseRestrictions: value,
      }));
    } else if (key === "doors") {
      setVenueFormState(prevState => ({
        ...prevState,
        doors: value
      }));
    } else if (key === "accountNumber") {
      setBankFormState(prevState => ({
        ...prevState,
        accountNumber: value,
      }));
    } else if (key === "beneficiaryName") {
      setBankFormState(prevState => ({
        ...prevState,
        beneficiaryName: value,
      }));
    } else if (key === "swiftCode") {
      setBankFormState(prevState => ({
        ...prevState,
        swiftCode: value,
      }));
    } else if (key === "sortCode") {
      setBankFormState(prevState => ({
        ...prevState,
        sortCode: value,
      }));
    } else if (key === "bankAddress") {
      setBankFormState(prevState => ({
        ...prevState,
        bankAddress: value,
      }));
    } else if (key === "internalId") {
      setCompanyFormState(prevState => ({
        ...prevState,
        internalId: value,
      }));
    } else if (key === "legalStructure") {
      setCompanyFormState(prevState => ({
        ...prevState,
        legalStructure: value,
      }));
    } else if (key === "expectPurchaseOrderNumber") {
      setCompanyFormState(prevState => ({
        ...prevState,
        expectPurchaseOrderNumber: value,
      }));
    }
    else {
      console.warn(`Unhandled key: ${key}`);
      return;
    }
  }

  const queryClient = useQueryClient();

  const onSuccessfulUpdate = async () => {
    queryClient.invalidateQueries(CompanyApi.CanReceivePeppol.getCacheKey(props.company.companyId));
    const newVenueDetails = await window.CoreApi.Venue.GetVenue(props.company.companyId);
    if (newVenueDetails.data.hasErrors) {
      return;
    }
    setVenueDetails(newVenueDetails.data.data.details);
    setVenueFormState(convertToVenueFormState(newVenueDetails.data.data.details));
    setCompany(newVenueDetails.data.data.company);
    setCompanyFormState(convertToFormState(newVenueDetails.data.data.company));
    setBankFormState(convertToBankFormState(newVenueDetails.data.data.company.bank));
  }

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    isInPageEditable={company.permissions.canModify}
    sections={[
      companyMainSection(company, companyFormState, bankFormState, tags),
      businessCentralSection(company, displayBusinessCentralNumber),
      hubSpotSection(company, displayHubSpotPanel),
      financialDetailSection(company, companyFormState, bankFormState),
      contactDetailSection(company, companyFormState, bankFormState),
      venueDetailsSection(company, venueDetails, venueFormState),
      bankDetailSection(company, companyFormState, bankFormState),
      {
        type: "custom",
        key: "companyMaps",
        render: () => <SectionPanel.Panel
          key="companyMaps"
          id="companyMaps"
          header={<SectionTemplateHeader>{RESX.Map.resxMap}</SectionTemplateHeader>}
          collapsible={{
            start: "open",
            uniqueRowId: "companyMaps",
            persist: true,
          }}
        >
          <CompanyGoogleMap company={company} />
        </SectionPanel.Panel>
      },
      recordInformation(
        company.createdUtc,
        company.createdUser,
        company.lastModifiedUtc,
        company.lastModifiedUser,
        company.companyId),
    ]}
  />
}